import { useEffect, useMemo, useState } from 'react';
import { Users } from 'lucide-react';
import { Button, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination, type ColumnDef, type FilterCondition, type FilterField } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { useEmployeeList, type EmployeeRecord, type EmployeeListParams } from '@/api/employees';
import { paginationView } from '@/pages/customers/pagination-summary';
import { EmployeeListToolbar } from './employee-list-toolbar';

const PAGE_SIZE = 20;

function useDebouncedValue<T>(value: T, delay: number): T {
  const [d, setD] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setD(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return d;
}

interface EmployeeListSectionProps {
  /** Fixed employee_status filter for this tab ([1]=active, [0]=resigned, undefined=all). */
  statusFilter?: number[];
  columns: ColumnDef<EmployeeRecord, unknown>[];
  filterFields?: FilterField[];
  onRowClick?: (row: EmployeeRecord) => void;
}

export function EmployeeListSection({
  statusFilter,
  columns,
  filterFields,
  onRowClick,
}: EmployeeListSectionProps) {
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(columns.map((c) => [c.id as string, true])),
  );

  const params = useMemo((): EmployeeListParams => {
    const p: EmployeeListParams = { page, page_size: PAGE_SIZE };
    if (query) p.q = query;
    if (statusFilter) p.employee_status = statusFilter;
    for (const cond of conditions) {
      if (cond.field === 'employee_contract_type') p.employee_contract_type = cond.value;
      if (cond.field === 'employee_gender') p.employee_gender = cond.value;
    }
    return p;
  }, [page, query, statusFilter, conditions]);

  const { data, isLoading, isError, isFetching, refetch } = useEmployeeList(params);

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const visibleColumns = useMemo(
    () => columns.filter((c) => visible[c.id as string] !== false),
    [columns, visible],
  );

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setPage(1);
    setFilterOpen(false);
  };

  const onSearch = (v: string) => {
    setRawQuery(v);
    setPage(1);
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <EmployeeListToolbar
        columns={columns}
        filterFields={filterFields}
        query={rawQuery}
        onQueryChange={onSearch}
        conditions={conditions}
        filterOpen={filterOpen}
        onFilterOpenChange={setFilterOpen}
        onApplyFilters={applyFilters}
        visible={visible}
        onToggleColumn={(id, checked) => setVisible((prev) => ({ ...prev, [id]: checked }))}
      />

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load employees"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </CardBody>
        </Card>
      ) : (
        <DataTable
          columns={visibleColumns}
          data={rows}
          getRowId={(row) => String(row.employee_id)}
          onRowClick={onRowClick}
          emptyState={
            <EmptyState
              icon={<Users size={24} />}
              title="No employees found"
              description="Try adjusting your search or filters."
            />
          }
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={`${view.summary}${isFetching ? ' …' : ''}`}
              />
            );
          })()}
        />
      )}
    </div>
  );
}
